<template>
  <ValidationObserver ref="observer" v-slot="{ invalid }">
    <v-form autocomplete="off" class="fill-height">
      <v-card class="fill-height d-flex flex-column">
        <v-toolbar dark color="primary" flat style="max-height: 64px;">
          <v-toolbar-title>
            Schedule Defect For
            <router-link
              class="accent--text text--lighten-1"
              :to="{name:'unit', params: { unitId: defect.unitId }}"
            >{{defect.unitNumber}}
            </router-link>
          </v-toolbar-title>
          <v-subheader class="subtitle">
            Submitted By: {{ defect.createdBy }}
          </v-subheader>
          <v-spacer></v-spacer>
          <slot name="header" v-if="!defect.isStale"></slot>
        </v-toolbar>

        <v-card-text class="flex-grow-1">
          <stale-data-alert :is-stale="defect.isStale || false" message="This defect has been updated by another user. Please refresh to see updated data." @refresh="refreshPage"></stale-data-alert>

          <v-container v-if="!defect.isStale">
            <v-row>
              <v-col cols="1">
                <v-text-field disabled v-model="defect.lineNumber" prefix="#"></v-text-field>
              </v-col>
              <v-col cols="11">
                <ValidationProvider name="Description" rules="required" v-slot="{ errors }">
                  <v-textarea
                    label="Describe the defect"
                    v-model="defect.description"
                    :error-messages="errors"
                    v-bind:disabled="!userIsFleetOrWorkshopManager"
                    auto-grow
                    rows="1"
                  ></v-textarea>
                </ValidationProvider>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <ValidationProvider 
                  name="Scheduled Reason" 
                  :rules="(userIsFleetManager || !editing) ? 'required' : ''"
                  v-slot="{ errors }">
                  <v-select
                    label="Scheduled Reason"
                    v-model="defect.scheduledReasonId"
                    item-value="id"
                    item-text="name"
                    :items="scheduledReasons"
                    :error-messages="errors"
                    v-bind:disabled="!userIsFleetManager && editing"
                  ></v-select>
                </ValidationProvider>
              </v-col>
              <v-col cols="8">
                <ValidationProvider name="Reason" rules="required" v-slot="{ errors }">
                  <v-textarea
                    label="Reason this defect was not completed"
                    v-model="defect.reason"
                    :error-messages="errors"
                    v-bind:disabled="!userIsFleetOrWorkshopManager"
                    auto-grow
                    rows="1"
                  ></v-textarea>
                </ValidationProvider>
              </v-col>
            </v-row>
            <v-row>
              <v-col :cols="defect.photoBlobUri ? 9 : 12">
                <v-row>
                  <v-col class="py-0" cols="4">
                    <ValidationProvider name="Rating" rules="required" v-slot="{ errors }">
                      <v-select label="Rating" v-model="defect.ratingId" item-value="id" item-text="name" :items="ratings" :error-messages="errors" @change="ratingChanged">
                        <template slot="item" slot-scope="data">
                          <v-chip x-small v-if="data.item.auditOnly" class="mr-1 px-2">Audit</v-chip>
                          {{ data.item.name }}
                        </template>
                      </v-select>
                    </ValidationProvider>
                  </v-col>
                  <v-col class="py-0" cols="4">
                    <ValidationProvider name="Source" rules="required" v-slot="{ errors }">
                      <v-select label="Source" v-model="defect.sourceId" item-value="id" item-text="name" :items="sources" :error-messages="errors" v-bind:disabled="!userIsFleetManager"></v-select>
                    </ValidationProvider>
                  </v-col>
                  <v-col class="py-0" cols="4">
                    <v-menu v-model="scheduledDateDialog" :close-on-content-click="false" max-width="290" v-bind:disabled="!userIsFleetOrWorkshopManager">
                      <template v-slot:activator="{ on }">
                        <ValidationProvider name="Expected Date" :rules="'date_format:' + dateDisplayFormat + '|date_between:' + formattedReceivedDate + ',' + formattedMaxDate + ',true'" v-slot="{ errors }">
                          <v-text-field
                            label="Expected Date"
                            v-model="formattedScheduledDate"
                            readonly
                            :error-messages="errors"
                            v-on="on"
                            append-icon="event"
                            :placeholder="'(' + defaultScheduledDate + ')'"
                            @click:append="scheduledDateDialog = true"
                            v-bind:disabled="!userIsFleetOrWorkshopManager"
                            :clearable="true"
                            @click:clear="defect.scheduledDate = null"
                          ></v-text-field>
                        </ValidationProvider>
                      </template>
                      <v-date-picker no-title v-model="defect.scheduledDate" :min="defect.receivedDate" :max="maxDate(defect)" @change="scheduledDateDialog = false"></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="py-0" cols="4">
                    <ValidationProvider name="Category" rules="required" v-slot="{ errors }">
                      <v-select label="Category" v-model="defect.categoryId" item-value="id" item-text="name" :items="categories" :error-messages="errors" v-bind:disabled="!userIsFleetOrWorkshopManager" @change="categoryUpdated"></v-select>
                    </ValidationProvider>
                  </v-col>
                  <v-col class="py-0" cols="4">
                    <ValidationProvider name="SubCategory" v-slot="{ errors }">
                      <v-select
                        label="Subcategory"
                        v-model="defect.subCategoryId"
                        item-value="id"
                        item-text="name"
                        :items="subCategories"
                        :error-messages="errors"
                        v-bind:disabled="!userIsFleetOrWorkshopManager"
                        @change="categoryUpdated"
                      ></v-select>
                    </ValidationProvider>
                  </v-col>
                  <v-col class="py-0" cols="4">
                    <ValidationProvider name="Assignees" v-slot="{ errors }">
                      <v-autocomplete label="Assignees" v-model="defect.assignees" item-value="id" item-text="displayName" :items="availableAssignees" :error-messages="errors" multiple readonly>
                        <template v-slot:item="data">
                          <v-list-item v-bind="data.attrs" v-on="data.on">
                            <v-list-item-action>
                              <v-checkbox :input-value="defect.assignees.includes(data.item.id)" color="black"></v-checkbox>
                            </v-list-item-action>
                            <v-list-item-content>
                              <v-list-item-title>{{ data.item.displayName }}</v-list-item-title>
                              <v-list-item-subtitle>{{ data.item.role }}</v-list-item-subtitle>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </ValidationProvider>
                  </v-col>
                </v-row>
                <v-row v-if="userIsFleetManager" class="mt-4">
                  <v-col class="py-0" cols="8">
                    <ValidationProvider name="ReasonForExtension" v-slot="{ errors }">
                      <v-textarea label="Reason for extension" v-model="defect.reasonForExtension" :error-messages="errors" @change="defect.fleetExtended = defect.reasonForExtension.length > 0" auto-grow rows="1"></v-textarea>
                    </ValidationProvider>
                  </v-col>
                  <v-col class="py-0" cols="4">
                    <v-menu v-model="extendUntilDateDialog" :close-on-content-click="false" max-width="290">
                      <template v-slot:activator="{ on }">
                        <ValidationProvider name="Expected Date" v-slot="{ errors }">
                          <v-text-field
                            label="Extend Until Date"
                            v-model="formattedExtensionDate"
                            readonly
                            :error-messages="errors"
                            v-on="on"
                            append-icon="event"
                            @click:append="extendUntilDateDialog = true"
                            :clearable="true"
                            @click:clear="defect.extensionDate = null"
                          ></v-text-field>
                        </ValidationProvider>
                      </template>
                      <v-date-picker no-title v-model="defect.extensionDate" :min="defect.receivedDate" @change="extendUntilDateDialog = false"></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row v-if="requiresVor || hasVor">
                  <v-col>
                    <v-alert dense border="left" type="warning" class="mb-0">Urgent and MOR Ratings require VOR </v-alert>
                  </v-col>
                </v-row>
                <v-row v-if="requiresVor || hasVor">
                  <v-col>
                    <vor-form ref="vor" :value="vor" :embedded="true" :disabled="hasVor" @edit="hasManuallyEditedVor = true"></vor-form>
                  </v-col>
                </v-row>
                <v-row>
                  <v-spacer></v-spacer>
                  <v-btn v-if="!otherLogVisible" color="tertiary" class="mb-2 mr-5" @click="toggleChildLog(defect)">
                    Logs
                    <v-icon small v-if="showChildLog">chevron_left</v-icon>
                    <v-icon small v-else>chevron_right</v-icon>
                  </v-btn>
              </v-row>
              </v-col>
              <v-col v-if="defect.photoBlobUri" cols="3" class="d-flex justify-center">
                <v-img
                  :src="defect.photoBlobUri"
                  max-width="300"
                  max-height="240"
                  contain
                  class="rounded elevation-2 mx-auto"
                  style="cursor: pointer"
                  @click="openPhotoModal(defect.photoBlobUri, defect.id)"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions v-if="!defect.isStale">
          <slot name="actions"></slot>
          <v-btn color="primary" :disabled="disableSubmit" @click="submit">Schedule</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <photo-modal
      ref="photoModal"
      :photo-url="photoModalData.photoUrl"
      :defect-id="photoModalData.defectId"
      @photo-deleted="handlePhotoDeleted"
    />
  </ValidationObserver>
</template>

<script>
import DefectsClient from '@/components/DefectNotices/DefectNoticesClient.vue';
import FormValidation from '@/components/FormValidation.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import moment from 'moment/moment';
import { DateFormat } from '@/plugins/date-filter';
import { DefectSourcesStore } from '@/stores/DefectSourcesStore';
import { DefectRatingsStore } from '@/stores/DefectRatingsStore';
import DefectHelper from '@/lib/DefectHelper';
import VorForm from '@/components/Units/Details/VehicleOffRoad/Form.vue';
import VehicleOffRoad from '@/components/Units/VehicleOffRoadClient.vue';
import UnitsClient from '@/components/Units/UnitsClient.vue';
import StaleDataAlert from '@/components/DefectNotices/StaleDataAlert.vue';
import PhotoModal from '@/components/PhotoModal.vue';
import { Event } from '@/plugins/bus';

export default {
  mixins: [DefectsClient, VehicleOffRoad, FormValidation, UnitsClient],
  components: {
    VorForm,
    ValidationProvider,
    ValidationObserver,
    StaleDataAlert,
    PhotoModal
  },
  props: {
    defect: {
      required: true
    }
  },
  created() {
    this.$bus.$on(Event.ParentLogVisibilityChanged, this.onParentLogVisibilityChanged);
    this.$bus.$on(Event.CloseAllLogs, this.onCloseAllLogs);
  },
  destroyed() {
    this.$bus.$off(Event.ParentLogVisibilityChanged, this.onParentLogVisibilityChanged);
    this.$bus.$off(Event.CloseAllLogs, this.onCloseAllLogs);
    this.showChildLog = false;
  },
  data() {
    return {
      sources: [],
      categories: [],
      subCategories: [],
      ratings: [],
      scheduledReasons: [],
      scheduledDateDialog: false,
      extendUntilDateDialog: false,
      numberData: [1, 2, 3, 4, 5, 6],
      today: moment().format(DateFormat.Canonical),
      todayFormatted: moment().format(DateFormat.DefaultDisplay),
      dateDisplayFormat: 'd MMMM yyyy',
      initiallyRequiresVor: false,
      hasManuallyEditedVor: false,
      vor: {},
      requiresVor: false,
      photoModalData: {
        photoUrl: '',
        defectId: null
      },
      showChildLog: false,
      otherLogVisible: false,
      originalAssignees: []
    };
  },
  methods: {
    async onParentLogVisibilityChanged(event) {
      // toggle my own defect visibility based on my parent's visibility
      this.otherLogVisible = event.visible;
    },
    toggleChildLog(defect) {
      this.showChildLog = !this.showChildLog;
      // let the parent know to show my log
      let event = { visible: this.showChildLog, defectId: defect.id, workSessions: defect.workSessions, assignees: this.originalAssignees, historicalAcceptedJobs: defect.historicalAcceptedJobs, defectNumber: defect.lineNumber };
      this.$bus.$emit(Event.ChildLogVisibilityChanged, event, defect);
    },
    async onCloseAllLogs() {
      this.showChildLog = false;
      this.otherLogVisible = false;
    },
    async init() {
      if (!this.$refs.observer) {
        return;
      }
      this.$refs.observer.reset();
      this.sources = await DefectSourcesStore.methods.getData();
      this.ratings = await DefectRatingsStore.methods.getData();
      this.categories = await this.getUnitDefectCategories(this.defect.unitId);
      this.availableAssignees = await this.getAvailableAssignees();
      this.scheduledReasons = await this.getScheduledReasons();

      if (this.defect.categoryId) {
        const category = this.categories.find(c => c.id === this.defect.categoryId);
        this.subCategories = category ? category.subCategories || [] : [];
      }

      this.vor = this.defect.vehicleOffRoad || {};
      if (this.defect.vehicleOffRoad) {
        this.hasManuallyEditedVor = true;
      }

      this.originalAssignees = Array.isArray(this.defect.assignees) ? this.defect.assignees.filter(a => typeof a === 'object') : [];
      this.defect.assignees = Array.isArray(this.defect.assignees) ? this.defect.assignees.map(a => (typeof a === 'string' ? a : a.id)) : [];

      this.showChildLog = false;
    },
    openPhotoModal(photoUrl, defectId) {
      this.photoModalData.photoUrl = photoUrl;
      this.photoModalData.defectId = defectId;
      this.$refs.photoModal.showModal();
    },
    handlePhotoDeleted() {
      this.defect.photoBlobUri = '';
      this.photoModalData.photoUrl = '';
      this.$refs.photoModal.close();
    },
    async submitForm() {
      this.submitted = true;
      const result = await this.$refs.observer.validate();
      const vorResult = this.$refs.vor ? await this.$refs.vor.checkValidation() : true;
      if (!result || !vorResult) {
        return;
      }

      if (!this.defect.scheduledDate) {
        this.defect.scheduledDate = this.defect.defaultScheduledDate;
      }

      this.$emit('schedule', { id: this.defect.id, model: { ...this.defect, isVor: this.requiresVor || this.hasVor, vor: this.vor } });
      setTimeout(() => (this.submitted = false), 1000);
    },
    reset() {
      this.$refs.observer.reset();
    },
    maxDate(defect) {
      let mxDate = moment();
      const category = this.categories.filter(c => c.id === this.defect.categoryId)[0] || { defaultScheduleMonths: 3 };
      const categoryMaxDate = moment(defect.receivedDate).add(category.defaultScheduleMonths, 'M');

      if (moment(categoryMaxDate) > moment(mxDate)) {
        mxDate = moment(categoryMaxDate);
      }
      if (defect.extensionDate) {
        mxDate = moment(defect.extensionDate) > moment(mxDate) ? moment(defect.extensionDate) : moment(mxDate);
      }

      return mxDate.format(DateFormat.Canonical);
    },
    async categoryUpdated() {
      await this.$refs.observer.reset();
      await this.$refs.observer.validate();
    },
    ratingChanged() {
      if (!this.defect) {
        this.requiresVor = false;
      }

      let vor = this.doesRatingIdRequireVor(this.defect.ratingId);
      this.requiresVor = !!vor;
    },
    doesRatingIdRequireVor(ratingId) {
      if (!this.ratings) {
        return false;
      }
      let rating = _.find(this.ratings, r => r.id === ratingId);
      if (!rating) {
        return false;
      }

      return rating.requiresOffRoad;
    },
    setVorValues() {
      if (!this.requiresVor || this.hasManuallyEditedVor) {
        return;
      }

      if (!this.defect.ratingId) {
        return;
      }

      let rating = _.find(this.ratings, r => r.id === this.defect.ratingId);
      if (!rating || !rating.requiresOffRoad) {
        return;
      }

      if (!this.defect.scheduledDate && !this.defect.categoryId) {
        return;
      }

      const date = this.defect.scheduledDate ? moment(this.defect.scheduledDate) : DefectHelper.defaultDateFor(this.defect.categoryId, this.categories, new Date(this.defect.receivedDate));
      if (date) {
        this.$refs.vor.setEndDate(moment(date).format(DateFormat.Canonical));
      }

      this.$refs.vor.setStartDate(moment(this.defect.receivedDate).format(DateFormat.Canonical));
    },
    async refreshPage() {
      try {
        // Emit an event to the parent to refresh the defect page
        this.$emit('refresh-page');

        // Close the modal
        this.$emit('close-modal');
      } catch (error) {
        this.$toast.error('Failed to refresh page');
      }
    }
  },
  computed: {
    disableSubmit() {
      return Object.keys(this.fields).some(key => this.fields[key].validated && this.fields[key].invalid);
    },
    formattedScheduledDate: {
      get() {
        return this.defect.scheduledDate ? moment(this.defect.scheduledDate).format(DateFormat.DefaultDisplay) : '';
      },
      set(newValue) {
        this.defect.scheduledDate = newValue;
      }
    },
    formattedExtensionDate: {
      get() {
        return this.defect.extensionDate ? moment(this.defect.extensionDate).format(DateFormat.DefaultDisplay) : '';
      },
      set(newValue) {
        this.defect.extensionDate = newValue;
      }
    },
    formattedReceivedDate() {
      return this.defect.receivedDate ? moment(this.defect.receivedDate).format(DateFormat.DefaultDisplay) : '';
    },
    formattedMaxDate() {
      let mxDate = moment();
      const category = this.categories.filter(c => c.id === this.defect.categoryId)[0] || { defaultScheduleMonths: 3 };
      const categoryMaxDate = moment(this.defect.receivedDate).add(category.defaultScheduleMonths, 'M');

      if (moment(categoryMaxDate) > moment(mxDate)) {
        mxDate = moment(categoryMaxDate);
      }
      if (this.defect.extensionDate) {
        mxDate = moment(this.defect.extensionDate) > moment(mxDate) ? moment(this.defect.extensionDate) : moment(mxDate);
      }

      return mxDate.format(DateFormat.DefaultDisplay);
    },
    defaultScheduledDate() {
      const d = DefectHelper.defaultDateFor(this.defect.categoryId, this.categories, new Date(this.defect.receivedDate));
      this.defect.defaultScheduledDate = d.format(DateFormat.Canonical);
      return d.format(DateFormat.DefaultDisplay);
    },
    hasVor() {
      return this.vor && !!this.vor.id;
    }
  },
  watch: {
    requiresVor(newVal) {
      if (newVal) {
        this.$nextTick(() => this.setVorValues());
      } else {
        this.vor = {};
      }
    },
    hasVor(newVal) {
      if (newVal) {
        this.$nextTick(() => this.setVorValues());
      } else {
        this.vor = {};
      }
    },
    'defect.ratingId': {
      async handler(newRatingId, oldRatingId) {
        this.$emit('ratingChanged', { newRatingId, oldRatingId });
      },
      immediate: true
    },
    'defect.categoryId': {
      async handler(categoryId) {
        this.subCategories = this.getUnitDefectSubCategories(categoryId);
      }
    }
  }
};
</script>
<style scoped>
  ::v-deep .v-toolbar__title {
    overflow: visible;
    text-overflow: unset;
    white-space: normal;
  }
</style>
